import React from 'react';
import { View, Pressable, Platform } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import { FontAwesome } from '@expo/vector-icons';
import { XStack } from 'tamagui';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Text } from '@constants/Themed';
import { LinearGradient } from 'expo-linear-gradient';
import * as Animatable from 'react-native-animatable';

export type ActionButton = {
  icon: string;
  onPress: () => void;
  disabled?: boolean;
  backgroundColor?: string;
  iconColor?: string;
};


const InputWrapper = ({
  children,
  onDragOver,
  onDragLeave,
  onDrop,
  draggedOver,
  style,
  theme,
  onVoiceButtonPress,
  isRecording,
  inputDisabled,
  onboardingInProgress,
  actionButtons,
  setChatMessages,
  clearConversationHistory,
  setAIHelp,
  setChatStarted
}: {
  children: React.ReactNode;
  onDragOver?: (e: any) => void;
  onDragLeave?: (e: any) => void;
  onDrop?: (e: any) => void;
  draggedOver?: boolean;
  style?: any;
  theme?: boolean;
  onVoiceButtonPress?: () => void;
  isRecording?: boolean;
  inputDisabled?: boolean;
  onboardingInProgress?: boolean;
  actionButtons?: ActionButton[];
  setChatMessages?: (messages: any[] | ((prev: any[]) => any[])) => void;
  clearConversationHistory?: () => void;
  setAIHelp?: (value: boolean) => void;
  setChatStarted?: (value: boolean) => void;
}) => {
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [draggedFileType, setDraggedFileType] = React.useState<'data' | 'image' | 'video' | 'document' | 'unknown'>('unknown');

  // Hide action buttons if container width is less than 250px
  const shouldHideActionButtons = containerWidth < 250;

  const handleContainerLayout = React.useCallback((event: any) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  }, []);

  const handleDragOverWithType = React.useCallback((e: any) => {
    // Detect file type from drag event
    if (e.dataTransfer?.items) {
      const items = Array.from(e.dataTransfer.items);
      const hasImage = items.some((item: any) => item.type?.startsWith('image/'));
      const hasVideo = items.some((item: any) => item.type?.startsWith('video/'));
      const hasData = items.some((item: any) =>
        item.type === 'text/csv' ||
        item.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        item.type === 'application/vnd.ms-excel'
      );
      const hasDocument = items.some((item: any) =>
        item.type === 'application/pdf' ||
        item.type === 'application/msword' ||
        item.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        item.type === 'application/vnd.ms-powerpoint' ||
        item.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        item.type === 'application/rtf' ||
        item.type === 'text/plain' ||
        item.type === 'text/rtf'
      );

      if (hasImage) {
        setDraggedFileType('image');
      } else if (hasVideo) {
        setDraggedFileType('video');
      } else if (hasData) {
        setDraggedFileType('data');
      } else if (hasDocument) {
        setDraggedFileType('document');
      } else {
        setDraggedFileType('unknown');
      }
    }
    onDragOver?.(e);
  }, [onDragOver]);

  const handleDragLeaveWithReset = React.useCallback((e: any) => {
    setDraggedFileType('unknown');
    onDragLeave?.(e);
  }, [onDragLeave]);

  const handleDropWithReset = React.useCallback((e: any) => {
    setDraggedFileType('unknown');
    onDrop?.(e);
  }, [onDrop]);

  if (Platform.OS === 'web') {
    return (
      <div
        onDragOver={handleDragOverWithType}
        onDragLeave={handleDragLeaveWithReset}
        onDrop={handleDropWithReset}
        onDragEnter={handleDragOverWithType}
        style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'visible' }}
      >
        <View
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, opacity: 0, pointerEvents: 'none' }}
          onLayout={handleContainerLayout}
        />
        {/* Action Buttons - Horizontal row at top (outside overflow container) */}
        {actionButtons && actionButtons.length > 0 && (
          <View
            style={{
              position: 'absolute',
              top: 13,
              left: 0,
              right: 0,
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              marginHorizontal: "35%",
              zIndex: 1000,
              pointerEvents: shouldHideActionButtons ? 'none' : 'auto',
              opacity: shouldHideActionButtons ? 0 : 1,
              display: shouldHideActionButtons ? 'none' : 'flex',
            } as any}
          >
            {actionButtons.map((button, index) => (
              <Pressable
                key={`action-button-${index}`}
                onPress={button.onPress}
                disabled={button.disabled}
                style={{
                  backgroundColor: button.backgroundColor || (theme ? '#ffffff' : '#1a1a1a'),
                  padding: 8,
                  borderRadius: 8,
                  minWidth: 32,
                  minHeight: 32,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: button.disabled ? 0.5 : 1,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.2,
                  shadowRadius: 4,
                  elevation: 3,
                }}
              >
                <AntDesign
                  name={button.icon as any}
                  size={16}
                  color={button.iconColor || (theme ? '#3b82f6' : '#60a5fa')}
                />
              </Pressable>
            ))}
          </View>
        )}
        <Animatable.View
          transition={['borderWidth', 'borderColor', 'backgroundColor', 'boxShadow']}
          duration={300}
          delay={0}
          style={[
            {
              flex: 1,
              borderWidth: draggedOver ? 3 : 1,
              borderColor: draggedOver
                ? '#3b82f6'
                : theme
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(255, 255, 255, 0.1)',
              borderStyle: draggedOver ? 'dashed' : 'solid',
              borderRadius: 20,
              backgroundColor: draggedOver
                ? 'rgba(59, 130, 246, 0.1)'
                : theme
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(30, 41, 59, 0.2)',
              // Glass morphism effects
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              boxShadow: theme
                ? '0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)'
                : '0 8px 32px 0 rgba(0, 0, 0, 0.3), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
              overflow: 'hidden',
              paddingVertical: 40,
              paddingHorizontal: 20
            },
            style
          ] as any}
        >
          {/* Inner glow effect - top highlight */}
          {Platform.OS === 'web' && (
            <LinearGradient
              colors={theme
                ? ['rgba(255, 255, 255, 0.15)', 'rgba(255, 255, 255, 0.05)', 'transparent']
                : ['rgba(255, 255, 255, 0.08)', 'rgba(255, 255, 255, 0.03)', 'transparent']
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '60%',
                pointerEvents: 'none',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
              }}
            />
          )}
          {/* Microphone Button */}
          <XStack style={{
            position: 'absolute',
            gap: 5,
            left: Platform.select({ native: 25, web: 19 }),
            top: Platform.select({ native: -25, web: 17 }),
          }}>
            <Pressable onPress={() => {
              //Start new chat with AI
              if (setChatMessages) setChatMessages([]);
              if (clearConversationHistory) clearConversationHistory();
              if (setAIHelp) setAIHelp(false);
              if (setChatStarted) setChatStarted(false);
            }} style={{
              backgroundColor: isRecording ? '#ef4444' : (inputDisabled || onboardingInProgress ? '#9ca3af' : '#3b82f6'),
              borderRadius: 25,
              width: 30,
              height: 30,
              zIndex: 100,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (inputDisabled || onboardingInProgress) && !isRecording ? 0.5 : 1,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
              elevation: 5,
            }}>
              <FontAwesome name="plus-circle" size={20} color={"white"} />
            </Pressable>
            {onVoiceButtonPress && (
              <Pressable
                onPress={onVoiceButtonPress}
                disabled={(inputDisabled || onboardingInProgress) && !isRecording}
                style={{
                  backgroundColor: isRecording ? '#ef4444' : (inputDisabled || onboardingInProgress ? '#9ca3af' : '#3b82f6'),
                  borderRadius: 25,
                  width: 30,
                  height: 30,
                  zIndex: 100,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (inputDisabled || onboardingInProgress) && !isRecording ? 0.5 : 1,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.25,
                  shadowRadius: 3.84,
                  elevation: 5,
                }}
              >
                <FontAwesome name="microphone" size={20} color="white" />
              </Pressable>
            )}
          </XStack>
          {children}
        </Animatable.View>
        {/* Drag overlay for images */}
        {draggedOver && draggedFileType === 'image' && Platform.OS === 'web' && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              borderRadius: 15,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              borderWidth: 3,
              borderColor: '#3b82f6',
              borderStyle: 'dashed',
              pointerEvents: 'none',
            }}
          >
            <View style={{ alignItems: 'center' }}>
              <MaterialCommunityIcons name="image-outline" size={48} color="#3b82f6" />
              <Text style={{ fontSize: 16, fontWeight: '600', marginTop: 8, color: '#3b82f6' }}>
                Drop image here
              </Text>
              <Text style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                JPG, PNG, GIF, WebP, SVG
              </Text>
            </View>
          </View>
        )}
        {/* Drag overlay for documents */}
        {draggedOver && draggedFileType === 'document' && Platform.OS === 'web' && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(16, 185, 129, 0.15)',
              borderRadius: 15,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              borderWidth: 3,
              borderColor: '#10b981',
              borderStyle: 'dashed',
              pointerEvents: 'none',
            }}
          >
            <View style={{ alignItems: 'center' }}>
              <MaterialCommunityIcons name="file-document-outline" size={48} color="#10b981" />
              <Text style={{ fontSize: 16, fontWeight: '600', marginTop: 8, color: '#10b981' }}>
                Drop document here
              </Text>
              <Text style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                PDF, Word, PowerPoint, Text files
              </Text>
            </View>
          </View>
        )}
      </div>
    );
  }

  return (
    <View style={[{ flex: 1, position: 'relative' }, style]} onLayout={handleContainerLayout}>
      {/* Action Buttons - Horizontal row at top for Native */}
      {actionButtons && actionButtons.length > 0 && (
        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            paddingHorizontal: 16,
            zIndex: 100,
            opacity: shouldHideActionButtons ? 0 : 1,
            pointerEvents: shouldHideActionButtons ? 'none' : 'auto',
          }}
        >
          {actionButtons.map((button, index) => (
            <Pressable
              key={`action-button-${index}`}
              onPress={button.onPress}
              disabled={button.disabled}
              style={{
                backgroundColor: button.backgroundColor || (theme ? '#ffffff' : '#1a1a1a'),
                padding: 8,
                borderRadius: 8,
                minWidth: 32,
                top: Platform.select({ native: -60 }),
                minHeight: 32,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: button.disabled ? 0.5 : 1,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <AntDesign
                name={button.icon as any}
                size={16}
                color={button.iconColor || (theme ? '#3b82f6' : '#60a5fa')}
              />
            </Pressable>
          ))}
        </View>
      )}
      {/* Reset Chat and Microphone Buttons for Native */}
      <XStack style={{
        position: 'absolute',
        gap: 5,
        left: Platform.select({ native: 30, web: 10 }),
        top: -18,
        zIndex: 100,
      }}>
        <Pressable onPress={() => {
          //Start new chat with AI
          if (setChatMessages) setChatMessages([]);
          if (clearConversationHistory) clearConversationHistory();
          if (setAIHelp) setAIHelp(false);
          if (setChatStarted) setChatStarted(false);
        }} style={{
          backgroundColor: (inputDisabled || onboardingInProgress) ? '#9ca3af' : '#3b82f6',
          borderRadius: 25,
          width: 30,
          height: 30,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: (inputDisabled || onboardingInProgress) ? 0.5 : 1,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }}>
          <FontAwesome name="plus-circle" size={20} color={"white"} />
        </Pressable>
        {onVoiceButtonPress && (
          <Pressable
            onPress={onVoiceButtonPress}
            disabled={(inputDisabled || onboardingInProgress) && !isRecording}
            style={{
              backgroundColor: isRecording ? '#ef4444' : (inputDisabled || onboardingInProgress ? '#9ca3af' : '#3b82f6'),
              borderRadius: 25,
              width: 30,
              height: 30,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (inputDisabled || onboardingInProgress) && !isRecording ? 0.5 : 1,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
              elevation: 5,
            }}
          >
            <FontAwesome name="microphone" size={20} color="white" />
          </Pressable>
        )}
      </XStack>
      {children}
    </View>
  );
}
export default InputWrapper;